/* shared/auth/UserMenu — top-right avatar dropdown shared by Platform and Org layouts. */
import React, { useState, useEffect, useRef } from 'react';
import { NexusIcon } from '../ui/NexusIcon';
import { Pill } from '../ui';

const getSessionUser = () => {
  if (window.__nx_auth?.user) return window.__nx_auth.user;
  try { return JSON.parse(localStorage.getItem('nx-session-v1')); } catch (_) { return null; }
};

export default function UserMenu({ roleLabel = 'Super Admin', roleTone = 'purple', onLogout, onProfile, dark = true }) {
  const [open, setOpen] = useState(false);
  const [user, setUser] = useState(getSessionUser);
  const ref = useRef(null);

  useEffect(() => {
    const h = (e) => setUser(e.detail?.user || null);
    window.addEventListener('nx-session-changed', h);
    return () => window.removeEventListener('nx-session-changed', h);
  }, []);

  useEffect(() => {
    if (!open) return;
    const close = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [open]);

  const name = user?.name || roleLabel;
  const email = user?.email || '';
  const initials = name.split(' ').map(w => w[0]).slice(0, 2).join('').toUpperCase() || 'SA';

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button
        onClick={() => setOpen(o => !o)}
        title={name}
        style={{
          display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer',
          background: dark ? 'rgba(255,255,255,0.08)' : 'var(--nx-surface-alt)',
          border: dark ? '1px solid rgba(255,255,255,0.12)' : '1px solid var(--nx-border)',
          borderRadius: 8, padding: '4px 10px 4px 4px',
          color: dark ? '#C8D4E8' : 'var(--nx-text)',
          fontSize: 12, fontWeight: 600,
        }}>
        <span style={{
          width: 26, height: 26, borderRadius: 6,
          background: 'linear-gradient(135deg,#4692EC,#125AB0)',
          display: 'grid', placeItems: 'center',
          color: '#fff', fontSize: 10, fontWeight: 800, flexShrink: 0,
        }}>{initials}</span>
        <span style={{ maxWidth: 110, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{name}</span>
        <NexusIcon name="ChevronDown" size={12} />
      </button>

      {open && (
        <div style={{
          position: 'absolute', right: 0, top: 'calc(100% + 6px)', zIndex: 400,
          background: '#fff', borderRadius: 10, width: 220,
          border: '1px solid var(--nx-border)', boxShadow: 'var(--nx-shadow-lg)',
          overflow: 'hidden',
        }}>
          <div style={{ padding: '12px 14px 10px', borderBottom: '1px solid var(--nx-divider)' }}>
            <div style={{ fontWeight: 700, fontSize: 13, color: '#1E293B', marginBottom: 2 }}>{name}</div>
            {email && <div style={{ fontSize: 11, color: 'var(--nx-text-muted)', marginBottom: 6 }}>{email}</div>}
            <Pill tone={roleTone}>{roleLabel}</Pill>
          </div>
          {onProfile && (
            <div style={{ padding: '6px 6px', borderBottom: '1px solid var(--nx-divider)' }}>
              <button style={menuItemStyle} onClick={() => { setOpen(false); onProfile(); }}>
                <NexusIcon name="User" size={14} /><span>Profile</span>
              </button>
            </div>
          )}
          <div style={{ padding: '6px 6px' }}>
            <button style={{ ...menuItemStyle, color: 'var(--nx-danger)' }} onClick={() => { setOpen(false); onLogout(); }}>
              <NexusIcon name="LogOut" size={14} /><span>Logout</span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

const menuItemStyle = {
  display: 'flex', alignItems: 'center', gap: 9,
  width: '100%', textAlign: 'left',
  padding: '7px 9px', borderRadius: 7,
  fontSize: 13, fontWeight: 500, color: '#334155',
  background: 'none', border: 'none', cursor: 'pointer',
};
